import {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {HelpIcon} from '@/components/help-icon'
import {DataTable, type Column} from '@/components/data-table'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {PlanLockedPage} from '@/components/plan-upsell'
import {Button} from '@/components/ui/button'
import {Label} from '@/components/ui/label'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {Skeleton} from '@/components/ui/skeleton'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {useOperatorPage} from '@/components/no-access-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useTableState} from '@/hooks/common/use-table-state'
import {
  CUARENTENA_PAGE_SIZE,
  useAbrirCuarentena,
  useCuarentena,
  usePedirSinHistoria,
  useSoltarCuarentena,
  type CuarentenaDocumento,
} from '@/hooks/salud/medicina_laboral/use-cuarentena-salud'
import {translateApiError} from '@/lib/errors'
import {formatDate, formatInstantDate} from '@/lib/risk'
import {tope} from '@/lib/campos-texto'

/** Las dos salidas son humanas: soltar exige que alguien con custodia lo revisó, y pedir a la IPS
 * el concepto no lo suelta ni detiene su reloj (§4). */
function DecidirDialog({
  businessId,
  documento,
  onCerrar,
}: {
  businessId: string
  documento: CuarentenaDocumento | null
  onCerrar: () => void
}) {
  const {t} = useTranslation()
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pedido, setPedido] = useState<string | null>(null)
  const soltar = useSoltarCuarentena(businessId)
  const pedir = usePedirSinHistoria(businessId)

  if (documento === null) return null

  return (
    <Dialog open onOpenChange={(open) => !open && onCerrar()}>
      <DialogContent data-testid="cuarentena-decidir-dialog">
        <DialogHeader>
          <DialogTitle>{t('cuarentena.decidir.titulo')}</DialogTitle>
          <DialogDescription>{t('cuarentena.decidir.descripcion')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cuarentena-motivo">{t('cuarentena.decidir.motivo')}</Label>
            <Textarea
              maxLength={tope('NOTA')}
              id="cuarentena-motivo"
              rows={3}
              value={motivo}
              data-testid="cuarentena-motivo"
              placeholder={t('cuarentena.decidir.motivoPlaceholder')}
              onChange={(event) => setMotivo(event.target.value)}
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" data-testid="cuarentena-decidir-error">
              {error}
            </p>
          )}
          {pedido && (
            <p className="text-sm text-primary" data-testid="cuarentena-pedido-ok">
              {t('cuarentena.decidir.pedidoEnviado', {correo: pedido})}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="cuarentena-soltar"
              disabled={motivo.trim().length === 0 || soltar.isPending}
              onClick={() => {
                setError(null)
                soltar.mutate(
                  {documentoId: documento.id, motivo: motivo.trim()},
                  {onSuccess: onCerrar, onError: (err) => setError(translateApiError(err))}
                )
              }}>
              {t('cuarentena.decidir.soltar')}
            </Button>
            <Button
              variant="outline"
              data-testid="cuarentena-pedir"
              disabled={pedir.isPending}
              onClick={() => {
                setError(null)
                pedir.mutate(documento.id, {
                  onSuccess: (data) => setPedido(data.enviado_a),
                  onError: (err) => setError(translateApiError(err)),
                })
              }}>
              {t('cuarentena.decidir.pedir')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** Ancla que se clica y no `window.open`: en un onSuccess, fuera del gesto, Safari la bloquea y el
 * registro de apertura contaría una entrega que no ocurrió. `noopener` aísla la sesión. */
function abrirEnPestana(data: {download_url: string}) {
  const enlace = document.createElement('a')
  enlace.href = data.download_url
  enlace.target = '_blank'
  enlace.rel = 'noopener'
  enlace.click()
}

/** Solo para el custodio (E1-H05): nunca muestra el contenido, solo la señal, la barrera y la supresión. */
export function CuarentenaPage() {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {businessId} = useParams()
  const {business, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const table = useTableState('suprimir_el')
  const custodia = capabilities.sgsst_custodiar_salud
  // Solo `page`: el endpoint no honra search ni sort; el orden es del servidor.
  const lista = useCuarentena(custodia ? businessId : undefined, {page: table.page, search: ''})
  const abrir = useAbrirCuarentena(businessId!)
  const [decidiendo, setDecidiendo] = useState<CuarentenaDocumento | null>(null)
  // Soltar el último de una página deja la lista vacía con total > 0: no es el estado vacío.
  const vaciaPeroHayMas = lista.data !== undefined && lista.data.items.length === 0 && lista.data.total > 0
  const setPage = table.setPage
  useEffect(() => {
    if (vaciaPeroHayMas) setPage(1)
  }, [vaciaPeroHayMas, setPage])
  // «No hay revisión automática» solo vale si ninguna retención la puso un lector (ia/ambas).
  const sinLector = !lista.data?.items.some((fila) => fila.barrera === 'ia' || fila.barrera === 'ambas')

  if (guard) return guard
  if (capabilities.plan_tier === 'free') {
    return (
      <PlanLockedPage feature={t('plans.feature.cuarentena')} businessId={businessId} businessName={business!.name} />
    )
  }
  // La custodia va después del plan y del acceso, con su propio bloque: al dueño operador solo le
  // falta esto, y NoAccessState le diría que no hace parte de la empresa.
  if (!custodia) {
    return (
      <div className="mx-auto max-w-2xl space-y-3" data-testid="cuarentena-solo-custodia">
        <h1 className="font-heading text-xl">{t('cuarentena.soloCustodia.titulo')}</h1>
        <p className="text-sm text-muted-foreground">{t('cuarentena.soloCustodia.cuerpo')}</p>
        <Button size="sm" variant="outline" onClick={() => navigate(`/businesses/${businessId}`)}>
          {t('cuarentena.soloCustodia.volver')}
        </Button>
      </div>
    )
  }

  // Soltar y abrir son actos sobre un dato de salud: el cinturón de modo dios los apaga
  // en la UI aunque la API se los conceda a la membresía real que haya detrás.
  const puedeActuar = !godReadOnly

  const columnas: Column<CuarentenaDocumento>[] = [
    {
      key: 'persona',
      header: t('cuarentena.tabla.persona'),
      cell: (fila) => (
        <span data-testid={`cuarentena-persona-${fila.id}`}>
          {fila.persona}
          <span className="block font-mono text-xs text-muted-foreground">{fila.filename}</span>
          {fila.pedido_a && fila.pedido_en && (
            <span className="block text-xs text-muted-foreground" data-testid={`cuarentena-pedido-${fila.id}`}>
              {t('cuarentena.pedidoFila', {correo: fila.pedido_a, fecha: formatInstantDate(fila.pedido_en)})}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'senal',
      header: t('cuarentena.tabla.senal'),
      width: 'w-1/4',
      cell: (fila) => (
        <CeldaEstado
          chip={
            <Tag tone="amber" data-testid={`cuarentena-motivo-${fila.id}`}>
              {t(`cuarentena.motivos.${fila.retencion_motivo}`)}
            </Tag>
          }
          meta={
            <span data-testid={`cuarentena-senal-${fila.id}`}>
              {t('cuarentena.seVio', {senal: fila.senal_vista})} · {t(`cuarentena.barreras.${fila.barrera}`)}
            </span>
          }
        />
      ),
    },
    {
      key: 'suprimir',
      header: t('cuarentena.tabla.suprimir'),
      width: 'w-32',
      cell: (fila) => (
        <span className="font-mono text-xs" data-testid={`cuarentena-suprimir-${fila.id}`}>
          {formatDate(fila.suprimir_el)}
        </span>
      ),
    },
    {
      key: 'aperturas',
      header: t('cuarentena.tabla.aperturas'),
      width: 'w-24',
      cell: (fila) => (
        <span className="font-mono text-xs" data-testid={`cuarentena-aperturas-${fila.id}`}>
          {fila.aperturas}
        </span>
      ),
    },
    {
      key: 'acciones',
      header: '',
      width: 'w-40',
      cell: (fila) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            disabled={!puedeActuar || abrir.isPending}
            data-testid={`cuarentena-abrir-${fila.id}`}
            onClick={() => abrir.mutate(fila.id, {onSuccess: abrirEnPestana})}>
            {t('cuarentena.abrir')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!puedeActuar}
            data-testid={`cuarentena-decidir-${fila.id}`}
            onClick={() => setDecidiendo(fila)}>
            {t('cuarentena.decidir.accion')}
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-5" data-testid="cuarentena">
      <PageHeading
        title={t('cuarentena.title')}
        subtitle={business!.name}
        ayuda={
          <HelpIcon label={t('common.help.iconLabelFor', {campo: t('cuarentena.title')})} testId="cuarentena-intro">
            <p>{t('cuarentena.intro')}</p>
            {sinLector && (
              <p className="mt-1.5" data-testid="cuarentena-sin-lector">
                {t('cuarentena.sinLector')}
              </p>
            )}
          </HelpIcon>
        }
      />

      {abrir.isError && (
        <p className="max-w-2xl text-sm text-destructive" data-testid="cuarentena-abrir-error">
          {translateApiError(abrir.error)}
        </p>
      )}

      {/* En salud «no pude leer» no es «no hay nada»: sin esto un 402, un 403 o una red caída dejan el esqueleto girando. */}
      {lista.isError && (
        <p className="max-w-2xl text-sm text-destructive" data-testid="cuarentena-error">
          {translateApiError(lista.error)}
        </p>
      )}
      {!lista.data && !lista.isError && <Skeleton className="h-40 w-full" />}
      {lista.data && (
        <>
          <DataTable
            columns={columnas}
            rows={lista.data.items}
            rowKey={(fila) => fila.id}
            emptyState={<span data-testid="cuarentena-vacio">{t('cuarentena.vacio')}</span>}
          />
          <PaginationBar
            page={table.page}
            pageSize={CUARENTENA_PAGE_SIZE}
            total={lista.data.total}
            onPageChange={table.setPage}
          />
        </>
      )}

      {/* `key` para remontarlo limpio: reabrirlo con el motivo de otra fila soltaría el documento equivocado. */}
      <DecidirDialog
        key={decidiendo?.id ?? 'cerrado'}
        businessId={businessId!}
        documento={decidiendo}
        onCerrar={() => setDecidiendo(null)}
      />
    </div>
  )
}
